import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertOctagon, Search, TrendingDown, Users, Truck, FlaskConical,
  FileSignature, ArrowRight, X, Plus, ShieldAlert, Sparkles, ArrowLeftRight,
  Download, Bell, Edit3, Check, BellOff,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/entity-loss`;

const RISK_COLORS = {
  high:   { bg: "bg-[#FF1744]/12", border: "border-[#FF1744]/40", text: "text-[#FF1744]" },
  medium: { bg: "bg-[#FFB800]/12", border: "border-[#FFB800]/40", text: "text-[#FFB800]" },
  low:    { bg: "bg-[#00E676]/12", border: "border-[#00E676]/40", text: "text-[#00E676]" },
};

const fmtEUR = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

export default function EntityLossSimulation() {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareList, setCompareList] = useState([]);   // array di nomi
  const [snapshot, setSnapshot] = useState(null);         // singola simulazione
  const [snapshots, setSnapshots] = useState(null);       // array simulazioni (compare)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [watchlist, setWatchlist] = useState([]);
  const loadWatchlist = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/watchlist`, { withCredentials: true });
      setWatchlist(r.data || []);
    } catch { setWatchlist([]); }
  }, []);
  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);

  // se arrivo con ?name=X, pre-avvia
  const initialName = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get("name") || "";
  }, [location.search]);

  const searchEntities = useCallback(async (q) => {
    try {
      const r = await axios.get(`${API}/search`, { params: { q }, withCredentials: true });
      setCandidates(r.data || []);
    } catch {
      setCandidates([]);
    }
  }, []);

  useEffect(() => {
    if (query.length >= 1) {
      const id = setTimeout(() => searchEntities(query), 200);
      return () => clearTimeout(id);
    }
    setCandidates([]);
  }, [query, searchEntities]);

  const runSingle = useCallback(async (name) => {
    setError("");
    setLoading(true);
    setSnapshots(null);
    setSnapshot(null);
    try {
      const r = await axios.get(`${API}/simulate`, { params: { name }, withCredentials: true });
      setSnapshot(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  }, []);

  const runCompare = useCallback(async () => {
    if (compareList.length < 2) return;
    setError("");
    setLoading(true);
    setSnapshot(null);
    setSnapshots(null);
    try {
      const r = await axios.post(`${API}/compare`, { names: compareList }, { withCredentials: true });
      setSnapshots(r.data?.entities || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  }, [compareList]);

  useEffect(() => {
    if (initialName) {
      setQuery(initialName);
      runSingle(initialName);
      navigate("/entity-loss-simulation", { replace: true });
    }
  }, [initialName, runSingle, navigate]);

  const pickEntity = (name) => {
    setShowDropdown(false);
    setQuery(name);
    if (compareMode) {
      setCompareList(prev => (prev.includes(name) ? prev : [...prev, name].slice(0, 3)));
      setQuery("");
    } else {
      runSingle(name);
    }
  };

  const removeFromCompare = (name) => {
    setCompareList(prev => prev.filter(n => n !== name));
  };

  return (
    <div className="min-h-screen bg-[#08090C] text-white pb-20" data-testid="entity-loss-page">
      {/* Header */}
      <div className="border-b border-white/[0.04] bg-[#0A0C10] sticky top-0 z-20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <AlertOctagon className="w-5 h-5 text-[#FF1744]" />
                <h1 className="text-xl font-bold tracking-tight text-white">{t("el.title")}</h1>
              </div>
              <p className="text-[11px] text-[#A0AEC0] max-w-2xl">{t("el.subtitle")}</p>
            </div>
            <button
              onClick={() => { setCompareMode(v => !v); setSnapshot(null); setSnapshots(null); }}
              className={`text-[10px] tracking-widest uppercase px-3 py-2 border font-bold flex items-center gap-2 ${
                compareMode
                  ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/8"
                  : "border-white/[0.08] text-[#A0AEC0] hover:text-white hover:border-white/[0.2]"
              }`}
              style={{ borderRadius: "2px" }}
              data-testid="el-compare-mode-toggle"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              {t("el.compare_mode")}
            </button>
          </div>

          {/* Search bar */}
          <div className="mt-4 relative">
            <div className="flex items-center gap-2 bg-[#08090C] border border-white/[0.08] px-3 py-2" style={{ borderRadius: "2px" }}>
              <Search className="w-4 h-4 text-[#6B7280]" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                placeholder={t("el.search_placeholder")}
                className="flex-1 bg-transparent text-sm text-white placeholder-[#6B7280] outline-none"
                data-testid="el-search-input"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-[#6B7280] hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {showDropdown && candidates.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 max-h-80 overflow-y-auto bg-[#0D0F14] border border-white/[0.08] shadow-2xl z-30" style={{ borderRadius: "2px" }} data-testid="el-search-dropdown">
                {candidates.map((c) => (
                  <button
                    key={c.normalized}
                    onClick={() => pickEntity(c.name)}
                    className="w-full px-3 py-2 hover:bg-white/[0.04] flex items-center justify-between gap-3 text-left"
                    data-testid={`el-search-result-${c.normalized}`}
                  >
                    <div>
                      <p className="text-sm text-white">{c.name}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {c.sources.map(s => (
                          <span key={s} className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 bg-white/[0.04] text-[#A0AEC0]" style={{ borderRadius: "2px" }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                    {compareMode ? (
                      <Plus className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-[#B39BFF] flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Compare pillbox */}
          {compareMode && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {compareList.map(n => (
                <div key={n} className="inline-flex items-center gap-2 bg-[#00E5FF]/8 border border-[#00E5FF]/30 px-2 py-1" style={{ borderRadius: "2px" }} data-testid={`el-compare-chip-${n}`}>
                  <span className="text-xs text-white">{n}</span>
                  <button onClick={() => removeFromCompare(n)} className="text-[#A0AEC0] hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {compareList.length >= 2 && (
                <button
                  onClick={runCompare}
                  className="text-[10px] tracking-widest uppercase font-bold px-3 py-1.5 bg-[#00E5FF] text-black hover:bg-white"
                  style={{ borderRadius: "2px" }}
                  data-testid="el-run-compare"
                >
                  {t("el.run_compare")}
                </button>
              )}
              <span className="text-[10px] text-[#6B7280] ml-2">
                {compareList.length}/3
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="text-center py-16 text-[#6B7280] text-sm" data-testid="el-loading">
            <Sparkles className="w-6 h-6 mx-auto mb-3 text-[#B39BFF] animate-pulse" />
            {t("el.loading")}
          </div>
        )}
        {error && !loading && (
          <div className="bg-[#FF1744]/10 border border-[#FF1744]/40 p-3 text-[#FF1744] text-sm" data-testid="el-error">{error}</div>
        )}
        {!loading && !error && !snapshot && !snapshots && (
          <div className="space-y-6" data-testid="el-empty">
            <div className="text-center py-10 text-[#6B7280] text-sm max-w-md mx-auto">
              <AlertOctagon className="w-8 h-8 mx-auto mb-3 text-[#6B7280]" />
              {t("el.no_selection")}
            </div>
            <WatchlistWidget list={watchlist} onRemoved={loadWatchlist} onSelect={(name) => { setQuery(name); runSingle(name); }} />
          </div>
        )}
        {snapshot && !loading && <SingleView snap={snapshot} onWatchlistChanged={loadWatchlist} />}
        {snapshots && !loading && (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${snapshots.length}, minmax(0,1fr))` }} data-testid="el-compare-grid">
            {snapshots.map((s, i) => <SingleView key={s.entity_name + i} snap={s} compact />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Single entity view — used in both single and compare modes
   ═════════════════════════════════════════════════════════════════ */
function SingleView({ snap, compact = false, onWatchlistChanged }) {
  const { t } = useLanguage();
  const [watchlisted, setWatchlisted] = useState(null);   // {id, threshold_eur} | null
  const [showThreshold, setShowThreshold] = useState(false);
  const [threshold, setThreshold] = useState("");

  useEffect(() => {
    if (!snap?.entity_name || compact) return;
    axios.get(`${API}/watchlist`, { withCredentials: true }).then(r => {
      const found = (r.data || []).find(w => w.normalized === snap.normalized_name);
      setWatchlisted(found || null);
      if (found) setThreshold(String(found.threshold_eur || ""));
    }).catch(() => {});
  }, [snap?.entity_name, snap?.normalized_name, compact]);

  const addToWatchlist = async () => {
    try {
      const th = Number(threshold) || 0;
      const r = await axios.post(`${API}/watchlist`, { entity_name: snap.entity_name, threshold_eur: th }, { withCredentials: true });
      setWatchlisted(r.data);
      setShowThreshold(false);
      onWatchlistChanged?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    }
  };
  const removeFromWatchlist = async () => {
    if (!watchlisted?.id) return;
    try {
      await axios.delete(`${API}/watchlist/${watchlisted.id}`, { withCredentials: true });
      setWatchlisted(null);
      onWatchlistChanged?.();
    } catch { /* noop */ }
  };
  const downloadPdf = () => {
    window.open(`${API}/report.pdf?name=${encodeURIComponent(snap.entity_name)}`, "_blank");
  };

  if (snap.error) {
    return (
      <div className="bg-[#FF1744]/10 border border-[#FF1744]/40 p-4 text-[#FF1744] text-sm" data-testid={`el-error-${snap.entity_name}`}>
        <strong>{snap.entity_name}</strong>: {snap.error}
      </div>
    );
  }
  const risk = RISK_COLORS[snap.risk_level] || RISK_COLORS.low;
  const totals = snap.totals || { at_risk_total_eur: 0, breakdown: {} };
  const bd = totals.breakdown || {};

  return (
    <div className="space-y-4" data-testid={`el-entity-${snap.normalized_name}`}>
      {/* HEADER card sticky-in-view */}
      <div className={`bg-[#0D0F14] border ${risk.border} p-5`} style={{ borderRadius: "2px" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-[#6B7280] mb-1">Entità</p>
            <h2 className="text-2xl font-bold text-white" data-testid={`el-entity-name-${snap.normalized_name}`}>{snap.entity_name}</h2>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 ${risk.bg} ${risk.text}`} style={{ borderRadius: "2px" }} data-testid={`el-risk-badge-${snap.normalized_name}`}>
                {t("el.risk_level")}: {t(`el.risk.${snap.risk_level}`)}
              </span>
              {snap.kyc_risk_status && (
                <span className="text-[10px] tracking-widest uppercase px-2 py-1 bg-white/[0.04] text-[#A0AEC0]" style={{ borderRadius: "2px" }}>
                  {t("el.kyc_status")}: {snap.kyc_risk_status}
                </span>
              )}
              {snap.sources_matched.map(s => (
                <span key={s} className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 border border-white/[0.08] text-[#B39BFF]" style={{ borderRadius: "2px" }}>{s}</span>
              ))}
            </div>
          </div>
          {/* Total at risk */}
          <div className="text-right">
            <p className="text-[10px] tracking-widest uppercase text-[#6B7280] mb-1">{t("el.total_at_risk")}</p>
            <p className="text-3xl font-bold text-white tabular-nums" data-testid={`el-total-${snap.normalized_name}`}>{fmtEUR(totals.at_risk_total_eur)}</p>
            {!compact && (
              <div className="flex items-center gap-2 mt-2 justify-end">
                <button onClick={downloadPdf}
                  className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 border border-white/[0.15] text-[#A0AEC0] hover:text-white hover:border-white/[0.35] flex items-center gap-1"
                  style={{ borderRadius: "2px" }}
                  data-testid={`el-download-pdf-${snap.normalized_name}`}
                >
                  <Download className="w-3 h-3" /> PDF
                </button>
                {watchlisted ? (
                  <button onClick={removeFromWatchlist}
                    className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 border border-[#FFB800]/40 text-[#FFB800] hover:text-white hover:bg-[#FFB800]/12 flex items-center gap-1"
                    style={{ borderRadius: "2px" }}
                    data-testid={`el-watchlist-remove-${snap.normalized_name}`}
                  >
                    <BellOff className="w-3 h-3" /> {t("el.watchlist.remove")}
                  </button>
                ) : showThreshold ? (
                  <div className="inline-flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] px-2 py-1" style={{ borderRadius: "2px" }}>
                    <span className="text-[10px] text-[#A0AEC0]">€</span>
                    <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)}
                      placeholder={t("el.watchlist.threshold_ph")}
                      className="bg-transparent w-24 text-xs text-white outline-none"
                      data-testid={`el-watchlist-threshold-input-${snap.normalized_name}`} />
                    <button onClick={addToWatchlist} className="text-[#00E676]" data-testid={`el-watchlist-save-${snap.normalized_name}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setShowThreshold(false)} className="text-[#A0AEC0]">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setShowThreshold(true)}
                    className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 border border-white/[0.15] text-[#A0AEC0] hover:text-white hover:border-white/[0.35] flex items-center gap-1"
                    style={{ borderRadius: "2px" }}
                    data-testid={`el-watchlist-add-${snap.normalized_name}`}
                  >
                    <Bell className="w-3 h-3" /> {t("el.watchlist.add")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Breakdown row */}
        <div className="mt-5 border-t border-white/[0.04] pt-4">
          <p className="text-[9px] tracking-widest uppercase text-[#6B7280] mb-2">{t("el.breakdown")}</p>
          <div className="grid grid-cols-3 gap-3">
            <BreakdownCell icon={FileSignature} color="#B39BFF" label={t("el.breakdown.contracts")} value={bd.contracts_eur} testid={`el-bd-contracts-${snap.normalized_name}`} />
            <BreakdownCell icon={TrendingDown} color="#00E5FF" label={t("el.breakdown.sales_open")} value={bd.sales_open_pipeline_eur} testid={`el-bd-sales-open-${snap.normalized_name}`} />
            <BreakdownCell icon={Users} color="#00E676" label={t("el.breakdown.sales_historic")} value={bd.sales_historic_12m_eur} testid={`el-bd-sales-hist-${snap.normalized_name}`} />
          </div>
        </div>
      </div>

      {/* Contratti */}
      <SectionCard icon={FileSignature} color="#B39BFF" title={t("el.sec.contracts")} count={snap.contracts.direct.length + snap.contracts.indirect.length} testid={`el-sec-contracts-${snap.normalized_name}`}>
        {(snap.contracts.direct.length + snap.contracts.indirect.length) === 0 ? (
          <p className="text-[11px] text-[#6B7280] italic px-3 py-4">{t("el.no_results")}</p>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {[...snap.contracts.direct, ...snap.contracts.indirect].map(c => (
              <li key={c.id || c.contract_id} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-white/[0.02]" data-testid={`el-contract-${c.id || c.contract_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-white truncate">{c.filename || "—"}</p>
                  {c.contract_type && <p className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">{c.contract_type}</p>}
                </div>
                <span className={`text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 ${c.link_type === "direct" ? "bg-[#00E5FF]/12 text-[#00E5FF]" : "bg-[#FFB800]/12 text-[#FFB800]"}`} style={{ borderRadius: "2px" }}>
                  {t(`el.link.${c.link_type}`)}{c.hops ? ` (${c.hops} ${t("el.link.hops")})` : ""}
                </span>
                <span className="text-xs tabular-nums text-white font-bold w-24 text-right">{c.contract_value != null ? fmtEUR(c.contract_value) : "—"}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-white/[0.04] px-3 py-2 flex justify-between text-[10px] text-[#A0AEC0]">
          <span>{t("el.link.direct")}: {fmtEUR(snap.contracts.exposure_direct_eur)} · {t("el.link.indirect")}: {fmtEUR(snap.contracts.exposure_indirect_eur)}</span>
          <span className="font-bold text-white tabular-nums">{fmtEUR(snap.contracts.exposure_total_eur)}</span>
        </div>
      </SectionCard>

      {/* Sales */}
      <SectionCard icon={TrendingDown} color="#00E5FF" title={t("el.sec.sales")} count={snap.sales.open_count} testid={`el-sec-sales-${snap.normalized_name}`}>
        {snap.sales.open_count === 0 ? (
          <p className="text-[11px] text-[#6B7280] italic px-3 py-4">{t("el.no_results")}</p>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {snap.sales.open_deals.map(d => (
              <li key={d.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${d.is_advanced ? "bg-[#FFB800]/6" : ""}`} data-testid={`el-deal-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-white truncate">{d.name}</p>
                  <p className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">{d.stage}{d.owner ? ` · ${d.owner}` : ""}</p>
                </div>
                {d.is_advanced && (
                  <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 bg-[#FFB800]/12 text-[#FFB800]" style={{ borderRadius: "2px" }}>
                    {t("el.deal.advanced")}
                  </span>
                )}
                <span className="text-xs tabular-nums text-white font-bold w-24 text-right">{fmtEUR(d.value_eur)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-white/[0.04] px-3 py-2 flex justify-between text-[10px] text-[#A0AEC0]">
          <span>Won 12m: {snap.sales.won_deals_last_12m.length}</span>
          <span className="font-bold text-white tabular-nums">{fmtEUR(snap.sales.open_pipeline_eur)}</span>
        </div>
      </SectionCard>

      {/* Supply Chain */}
      <SectionCard icon={Truck} color="#FFB800" title={t("el.sec.supply")} count={snap.supply_chain.critical_products_count} testid={`el-sec-supply-${snap.normalized_name}`}>
        {!snap.supply_chain.is_supplier ? (
          <p className="text-[11px] text-[#6B7280] italic px-3 py-4">{t("el.supply.not_a_supplier")}</p>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {snap.supply_chain.products_supplied.map(p => (
              <SupplyRow key={p.marker_id} p={p} readOnly={compact} />
            ))}
          </ul>
        )}
      </SectionCard>

      {/* R&D */}
      <SectionCard icon={FlaskConical} color="#00E676" title={t("el.sec.rd")} count={snap.rd.total_projects} testid={`el-sec-rd-${snap.normalized_name}`}>
        {snap.rd.total_projects === 0 ? (
          <p className="text-[11px] text-[#6B7280] italic px-3 py-4">{t("el.rd.no_data")}</p>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {snap.rd.patents.map(p => (
              <RDRow key={"p_" + p.id} item={p} kind="patent" nkey={snap.normalized_name} readOnly={compact} />
            ))}
            {snap.rd.technologies.map(tk => (
              <RDRow key={"t_" + tk.id} item={tk} kind="technology" nkey={snap.normalized_name} readOnly={compact} />
            ))}
            {snap.rd.equipment.map(e => (
              <RDRow key={"e_" + e.id} item={e} kind="equipment" nkey={snap.normalized_name} readOnly={compact} />
            ))}
          </ul>
        )}
      </SectionCard>

      {/* LLM narrative footer */}
      {snap.llm_narrative && !compact && (
        <div className="bg-[#7C3AED]/6 border border-[#7C3AED]/25 p-4" style={{ borderRadius: "2px" }} data-testid={`el-narrative-${snap.normalized_name}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#B39BFF]" />
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#B39BFF]">{t("el.narrative_title")}</p>
          </div>
          <p className="text-sm text-white leading-relaxed">{snap.llm_narrative}</p>
          <p className="text-[10px] text-[#6B7280] italic mt-2">{t("el.narrative_disclaimer")}</p>
        </div>
      )}
    </div>
  );
}

function BreakdownCell({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.04] px-3 py-2" style={{ borderRadius: "2px" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-[9px] tracking-widest uppercase text-[#A0AEC0] truncate">{label}</p>
      </div>
      <p className="text-lg font-bold tabular-nums text-white">{fmtEUR(value)}</p>
    </div>
  );
}

function SectionCard({ icon: Icon, color, title, count, testid, children }) {
  return (
    <div className="bg-[#0D0F14] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <p className="text-[10px] tracking-widest uppercase font-bold" style={{ color }}>{title}</p>
        </div>
        <span className="text-[10px] tabular-nums text-[#A0AEC0]">{count}</span>
      </div>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Supply Chain row with inline editing (alternatives + lead time)
   ───────────────────────────────────────────────────────────── */
function SupplyRow({ p, readOnly }) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [alt, setAlt] = useState(p.alternative_suppliers_count ?? "");
  const [lead, setLead] = useState(p.substitution_lead_days ?? "");
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(p);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/supply/${p.marker_id}`, {
        alternative_suppliers_count: alt === "" ? null : Number(alt),
        substitution_lead_days: lead === "" ? null : Number(lead),
      }, { withCredentials: true });
      setLocal(prev => ({
        ...prev,
        alternative_suppliers_count: r.data.alternative_suppliers_count,
        substitution_lead_days: r.data.substitution_lead_days,
      }));
      setEditing(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const hasAlt = local.alternative_suppliers_count != null && local.alternative_suppliers_count > 0;
  const altUnknown = local.alternative_suppliers_count == null;

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`el-supply-${p.marker_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white truncate">{local.name}</p>
        {local.category && <p className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">{local.category}</p>}
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input type="number" min="0" value={alt} onChange={e => setAlt(e.target.value)}
            className="w-14 bg-[#0A0C10] border border-white/[0.06] px-1 py-0.5 text-[11px] text-white text-center"
            style={{ borderRadius: "2px" }} placeholder="# alt"
            data-testid={`el-supply-alt-input-${p.marker_id}`} />
          <input type="number" min="0" value={lead} onChange={e => setLead(e.target.value)}
            className="w-14 bg-[#0A0C10] border border-white/[0.06] px-1 py-0.5 text-[11px] text-white text-center"
            style={{ borderRadius: "2px" }} placeholder="gg"
            data-testid={`el-supply-lead-input-${p.marker_id}`} />
          <button onClick={save} disabled={busy} className="text-[#00E676] disabled:opacity-40" data-testid={`el-supply-save-${p.marker_id}`}><Check className="w-3.5 h-3.5" /></button>
          <button onClick={() => { setEditing(false); setAlt(local.alternative_suppliers_count ?? ""); setLead(local.substitution_lead_days ?? ""); }} className="text-[#A0AEC0]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <>
          <span className={`text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 ${
            altUnknown ? "bg-white/[0.04] text-[#6B7280]"
            : hasAlt ? "bg-[#00E676]/12 text-[#00E676]"
            : "bg-[#FF1744]/12 text-[#FF1744]"
          }`} style={{ borderRadius: "2px" }}>
            {altUnknown ? t("el.na") : hasAlt ? `${local.alternative_suppliers_count} ${t("el.supply.alt_available")}` : t("el.supply.no_alt")}
          </span>
          <span className="text-[10px] text-[#A0AEC0] w-20 text-right">
            {local.substitution_lead_days != null ? `${local.substitution_lead_days} ${t("el.days")}` : t("el.na")}
          </span>
          {!readOnly && (
            <button onClick={() => setEditing(true)} className="text-[#A0AEC0] hover:text-[#00E5FF]" data-testid={`el-supply-edit-${p.marker_id}`}>
              <Edit3 className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </li>
  );
}

/* ─────────────────────────────────────────────────────────────
   Watchlist widget — mostrato nella empty state
   ───────────────────────────────────────────────────────────── */
function WatchlistWidget({ list, onRemoved, onSelect }) {
  const { t } = useLanguage();
  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/watchlist/${id}`, { withCredentials: true });
      onRemoved?.();
    } catch { /* noop */ }
  };

  return (
    <div className="max-w-3xl mx-auto bg-[#0D0F14] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="el-watchlist-widget">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#FFB800]" />
          <p className="text-[10px] tracking-widest uppercase font-bold text-[#FFB800]">{t("el.watchlist.title")}</p>
        </div>
        <span className="text-[10px] text-[#A0AEC0]">{list.length}/5</span>
      </div>
      {list.length === 0 ? (
        <p className="text-[11px] text-[#6B7280] italic px-3 py-6 text-center">{t("el.watchlist.empty")}</p>
      ) : (
        <ul className="divide-y divide-white/[0.04]">
          {list.map(w => (
            <li key={w.id}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.02] ${w.alert_active ? "bg-[#FF1744]/6" : ""}`}
              onClick={() => onSelect(w.entity_name)}
              data-testid={`el-watchlist-row-${w.normalized}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white truncate">{w.entity_name}</span>
                  {w.alert_active && (
                    <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 bg-[#FF1744]/20 text-[#FF1744] flex items-center gap-1" style={{ borderRadius: "2px" }}>
                      <AlertOctagon className="w-3 h-3" /> {t("el.watchlist.alert")}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-[#A0AEC0]">
                  {t("el.watchlist.exposure")}: <span className="tabular-nums text-white">{fmtEUR(w.current_exposure_eur)}</span>
                  {" · "}
                  {t("el.watchlist.threshold")}: <span className="tabular-nums">{fmtEUR(w.threshold_eur)}</span>
                </p>
              </div>
              <span className={`text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 ${RISK_COLORS[w.risk_level]?.bg} ${RISK_COLORS[w.risk_level]?.text}`} style={{ borderRadius: "2px" }}>
                {t(`el.risk.${w.risk_level}`)}
              </span>
              <button onClick={(e) => remove(w.id, e)} className="text-[#A0AEC0] hover:text-[#FF1744]" data-testid={`el-watchlist-del-${w.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RDRow({ item, kind, readOnly }) {
  const { t } = useLanguage();
  const [crit, setCrit] = useState(item.criticality || "normal");
  const [busy, setBusy] = useState(false);
  const label = { patent: "Patent", technology: "Technology", equipment: "Equipment" }[kind];
  const meta = kind === "patent"
    ? `${label} · ${item.status || "—"}`
    : kind === "technology"
      ? `${label} · ${item.category || "—"}${item.trl ? ` · TRL ${item.trl}` : ""}`
      : `${label} · ${item.category || "—"}`;
  const title = item.title || item.name;

  const save = async (next) => {
    setBusy(true);
    try {
      await axios.put(`${API}/rd/${kind}/${item.id}/criticality`, { criticality: next }, { withCredentials: true });
      setCrit(next);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const isCritical = crit === "critical" || crit === "unique";
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`el-rd-${kind}-${item.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white truncate">{title}</p>
        <p className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">{meta}</p>
      </div>
      {isCritical && (
        <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 bg-[#FF1744]/12 text-[#FF1744]" style={{ borderRadius: "2px" }}>
          <ShieldAlert className="w-3 h-3 inline mr-0.5" /> {crit === "unique" ? t("el.rd.unique") : "Critica"}
        </span>
      )}
      {!readOnly ? (
        <select value={crit} onChange={e => save(e.target.value)} disabled={busy}
          className="bg-[#0A0C10] border border-white/[0.06] px-1 py-0.5 text-[10px] text-white"
          style={{ borderRadius: "2px" }}
          data-testid={`el-rd-crit-select-${kind}-${item.id}`}
        >
          <option value="normal">Normale</option>
          <option value="critical">Critica</option>
          <option value="unique">Unica</option>
        </select>
      ) : (
        !isCritical && <span className="text-[9px] tracking-widest uppercase text-[#6B7280]">{t("el.na")}</span>
      )}
    </li>
  );
}

